import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { string } from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Dropdown } from '@atoms';
import { DateSelectingModal, ListSelectingModal } from '@molecules';
import { Onboarding } from '@templates';
import { Flex, Typography } from '@mixins';
import { theme } from '@styles';
import { routes } from '@constants';
import { ONBOARDING_STEPS } from '@constants/common';
import { getDateLabel, formatTimeValue, getTimeList } from '@helpers/time';
import { dayjs } from '@utils';
import { updateCompanyOnboarding } from '@store/company/duck';

import ScheduleOrientationOnboardingImage from '@assets/images/ScheduleOrientationOnboarding.png';

const ScheduleOrientationOnboarding = ({ direction }) => {
  const { currentLocation } = useSelector(store => store.locationStore);
  const { onboardingState } = useSelector(store => store.companyStore);

  // timeZone is a dependency because dayjs renders in the location's timezone, which is only
  // set once the location request resolves — without it the state keeps the browser's timezone
  const orientationDate = useMemo(
    () => (onboardingState?.orientationDate ? dayjs(onboardingState?.orientationDate) : null),
    [onboardingState?.orientationDate, currentLocation?.timeZone],
  );

  const dispatch = useDispatch();
  const history = useHistory();

  const [date, setDate] = useState(orientationDate ?? null);
  const [time, setTime] = useState(orientationDate ? orientationDate.format('HH:mm') : null);

  useEffect(() => {
    if (orientationDate) {
      setDate(orientationDate);
      setTime(orientationDate.format('HH:mm'));
    }
  }, [orientationDate]);

  const [isDateModalOpen, setIsDateModalOpen] = useState(false);
  const [isTimeModalOpen, setIsTimeModalOpen] = useState(false);

  const pickersRef = useRef();

  const isSelectionIncomplete = (!!date && !time) || (!date && !!time);

  useEffect(() => {
    if (!isDateModalOpen && !isTimeModalOpen) {
      return undefined;
    }

    const handleOutsideClick = event => {
      if (pickersRef.current && !pickersRef.current.contains(event.target)) {
        setIsDateModalOpen(false);
        setIsTimeModalOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);

    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isDateModalOpen, isTimeModalOpen]);

  const times = useMemo(
    () => getTimeList(date ?? dayjs()).filter(timeSlot => timeSlot.$H >= 10 && timeSlot.$H <= 16),
    [date],
  );

  // Time is stored as a canonical 'HH:mm' value; the location's format only affects how it is displayed
  const timeOptions = useMemo(
    () =>
      times.map(timeline => {
        const value = timeline.format('HH:mm');

        return { label: formatTimeValue(value, currentLocation?.timeFormat), value };
      }),
    [currentLocation?.timeFormat, times],
  );

  const timeLabel = useMemo(
    () => formatTimeValue(time, currentLocation?.timeFormat),
    [currentLocation?.timeFormat, time],
  );

  const handleDateModalChangeState = useCallback(() => {
    setIsDateModalOpen(prevValue => {
      if (!prevValue) {
        setIsTimeModalOpen(false);
      }
      return !prevValue;
    });
  }, []);

  const handleTimeModalChangeState = useCallback(() => {
    setIsTimeModalOpen(prevValue => {
      if (!prevValue) {
        setIsDateModalOpen(false);
      }

      return !prevValue;
    });
  }, []);

  const handleDateSelect = useCallback(
    selectedDate => {
      const offset = dayjs().$offset ?? 0;

      setDate(
        dayjs(selectedDate)
          .add(-(offset + new Date(selectedDate).getTimezoneOffset()), 'minutes')
          .add(dayjs().$u ? -new Date(selectedDate).getTimezoneOffset() : 0, 'minute'),
      );

      setIsDateModalOpen(false);
    },
    [dayjs().$offset, dayjs().$u, new Date().getTimezoneOffset()],
  );

  const handleTimeSelect = newTime => () => {
    handleTimeModalChangeState();
    setTime(newTime);
  };

  const handleClear = useCallback(() => {
    setDate(null);
    setTime(null);
    setIsDateModalOpen(false);
    setIsTimeModalOpen(false);
  }, []);

  // Both set saves the booking, both empty cancels it, a half-filled pair is left untouched
  const getOrientationDatePayload = () => {
    if (date && time) {
      return {
        orientationDate: date.set('hours', time.split(':')[0]).set('minutes', time.split(':')[1]).toISOString(),
      };
    }

    if (!date && !time) {
      return { orientationDate: null };
    }

    return {};
  };

  const isWeekendDisabled = useCallback(
    ({ date: tileDate, view }) => {
      if (view !== 'month') {
        return false;
      }

      const dayOfWeek = tileDate.getDay();
      const isIsraelLocation = currentLocation?.country === 'Israel';

      // Israel weekend: Friday (5) & Saturday (6); elsewhere: Saturday (6) & Sunday (0)
      return isIsraelLocation ? dayOfWeek === 5 || dayOfWeek === 6 : dayOfWeek === 0 || dayOfWeek === 6;
    },
    [currentLocation?.country],
  );

  const handleUpdateDate = () => {
    dispatch(
      updateCompanyOnboarding({
        currentStep: ONBOARDING_STEPS.SCHEDULE_ORIENTATION,
        ...getOrientationDatePayload(),
      }),
    );
  };

  const handleBack = () => {
    handleUpdateDate();

    history.push(routes.arrivalDateOnboarding, { direction: 'back' });
  };

  const handleNavigateNext = () => {
    history.push(routes.companyProfileOnboarding, { direction: 'forward' });
  };

  const handleNext = () => {
    dispatch(
      updateCompanyOnboarding({
        currentStep: ONBOARDING_STEPS.SCHEDULE_ORIENTATION,
        nextStep: ONBOARDING_STEPS.COMPANY_PROFILE,
        ...getOrientationDatePayload(),
      }),
    );

    handleNavigateNext();
  };

  return (
    <Onboarding
      onSaveAndContinueLater={handleUpdateDate}
      isNextButtonDisabled={isSelectionIncomplete}
      onNext={handleNext}
      direction={direction}
      title="Schedule Orientation"
      image={ScheduleOrientationOnboardingImage}
      showNavigationItems
      currentStep={5}
      onBack={handleBack}>
      <Typography
        variant="diatype-300"
        mb={30}
        fontSize={[14, 16, 20]}
        color={theme.color.mintMinded[900]}
        lineHeight="1.6"
        maxWidth="600px">
        We’d love to welcome your team with a 30-minute orientation session during your first week. Pick a date/time
        that works best
      </Typography>
      <Flex flexDirection="column" mb={50}>
        <Flex ref={pickersRef} position="relative" flexDirection="row">
          <Flex mr={['30px', '50px', '50px']} marginBottom="0px">
            <Dropdown
              onDropdownClick={handleDateModalChangeState}
              value={date ? getDateLabel(date) : null}
              defaultValue="Date"
              alignSelf="end"
              title="Date"
              position="relative"
              width={['140px', '200px', '200px']}>
              {isDateModalOpen && (
                <DateSelectingModal
                  minDate={dayjs(onboardingState?.arrivalDate ?? onboardingState?.originalMoveInDate ?? dayjs()).$d}
                  canBeClosed={false}
                  hasSelectingButton={false}
                  onSelect={handleDateSelect}
                  onModalClose={handleDateModalChangeState}
                  onClear={handleClear}
                  clearLabel="Clear"
                  calendarDate={date?.$d ?? null}
                  tileDisabled={isWeekendDisabled}
                  type="month"
                  topPosition={70}
                />
              )}
            </Dropdown>
          </Flex>
          <Flex flexDirection="column" width={['150px', '200px', '200px']} alignSelf="end">
            <Dropdown
              onDropdownClick={handleTimeModalChangeState}
              value={timeLabel}
              defaultValue="Time"
              alignSelf="end"
              title="Time"
              position="relative"
              width="100%">
              {isTimeModalOpen && (
                <ListSelectingModal
                  canBeClosed={false}
                  hasSelectingButton={false}
                  items={timeOptions}
                  selectedItem={time}
                  onSelect={handleTimeSelect}
                  onModalClose={handleTimeModalChangeState}
                  onClear={handleClear}
                  clearLabel="Clear"
                  topPosition={70}
                />
              )}
            </Dropdown>
          </Flex>
        </Flex>
        <Flex mt="8px" minHeight="18px">
          {isSelectionIncomplete && (
            <Typography fontSize={12} variant="diatype-400" color={theme.color.red}>
              *Please select both a date and a time
            </Typography>
          )}
        </Flex>
      </Flex>
    </Onboarding>
  );
};

ScheduleOrientationOnboarding.defaultProps = {
  direction: null,
};

ScheduleOrientationOnboarding.propTypes = {
  direction: string,
};

export default ScheduleOrientationOnboarding;
